/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL } from '@/config/serviceAPI';
import { API_URLS } from '@/constants/apiUrls';
import { createQuery } from '@/utils/createReactQuery';
import { getAxios } from '@/lib/axios';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { useCountry, useLocale } from '@/hooks/useLocale';
import { useIsBot } from '@/utils/useIsBot';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { type AxiosInstance } from 'axios';
import { type CertonaPricing } from '@/types';
import { type ProductModelACES } from '@/api/types/products-types';
import type {
  AffinityRelatedProductsOptionsACES,
  AffinityRelatedProductsResponseACES,
  AffinityRelatedProductsData,
  AffinityRelatedProductACES,
} from '../interface';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useMemo } from 'react';
import { sessionStorage } from '@/utils/sessionStorage';
import { countryCodes } from '@/constants/locale';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { useOMSFlags } from '@/features/product/utils/getOMSFlags';
import { useFeatureFlag } from '@/features/globalConfig/hooks/useFeatureFlag';
import { getAffinityRelatedProductsResultsGCP } from './getAffinityRelatedProductsGCP';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURL}/ecomm/b2c/v1/browse/v3/affinity/related-parts`;

export const getAffinityRelatedProductsResultsACES = async (
  options: AffinityRelatedProductsOptionsACES,
  axiosInstance?: AxiosInstance,
) => {
  let certonaPricing = null;
  const {
    data: { affinityRelatedProducts },
  } = await getAxios(axiosInstance).get<AffinityRelatedProductsResponseACES>(URL, {
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
    params: options,
  });
  const affinityData = affinityRelatedProducts.slice(
    0,
    options?.recordsPerPage || affinityRelatedProducts.length,
  );

  if (affinityData.length) {
    const skuNumbers = affinityData.map((part) => part.skuId).join(',');
    const { data: certonaPricingForAffinityItems } = await getAxios(axiosInstance).get<
      CertonaPricing[]
    >(`${API_URLS.CERTONA_PRICING}${skuNumbers}?storeNumber=${options.storeId ?? ''}`);

    certonaPricing = certonaPricingForAffinityItems;
  }

  return selector(affinityData, certonaPricing);
};

const selector = (
  affinityData: AffinityRelatedProductACES[],
  certonaPricing: CertonaPricing[] | null,
): AffinityRelatedProductsData => {
  if (certonaPricing) {
    const relatedProductsWithPricing = affinityData.map((product) => {
      const pricingForProduct = certonaPricing.find((part) => part.skuId === product.skuId);
      const {
        productDetailsPageUrl: seoUrl,
        partGroupId: productId,
        ...affinityRelatedProduct
      } = product;

      if (!pricingForProduct) {
        // eslint-disable-next-line no-console
        console.error(`Missing Certona pricing for product SKU: ${product.skuId}`);
        return null;
      }

      return {
        ...affinityRelatedProduct,
        ...pricingForProduct,
        productId,
        seoUrl,
      };
    });

    return relatedProductsWithPricing.filter(Boolean) as AffinityRelatedProductsData;
  }

  return null;
};

type PendingAffinityPrefetch = {
  promise: Promise<AffinityRelatedProductsData>;
  partGroupId: string;
};

let _pendingAffinityPrefetch: PendingAffinityPrefetch | null = null;

export const startAffinityCartPrefetch = (
  options: AffinityRelatedProductsOptionsACES,
  useGCPRelatedPartsService: boolean,
): void => {
  const fetchFn = useGCPRelatedPartsService
    ? getAffinityRelatedProductsResultsGCP
    : getAffinityRelatedProductsResultsACES;
  _pendingAffinityPrefetch = {
    promise: fetchFn(options),
    partGroupId: options.partGroupId,
  };
};

export const getAffinityForCart = async (
  options: AffinityRelatedProductsOptionsACES,
  useGCPRelatedPartsService = false,
): Promise<AffinityRelatedProductsData> => {
  const pending = _pendingAffinityPrefetch;
  _pendingAffinityPrefetch = null;
  if (pending?.partGroupId === options.partGroupId) {
    return pending.promise;
  }
  const fetchFn = useGCPRelatedPartsService
    ? getAffinityRelatedProductsResultsGCP
    : getAffinityRelatedProductsResultsACES;
  return fetchFn(options);
};

const { useData: useAffinityData } = createQuery<
  AffinityRelatedProductsData,
  AffinityRelatedProductsOptionsACES
>('affinityRelatedParts', async (options, axiosInstance) => {
  const { useGCPRelatedPartsService: gcpFlag, ...restOptions } =
    options as AffinityRelatedProductsOptionsACES & { useGCPRelatedPartsService?: boolean };
  if (gcpFlag) {
    return getAffinityRelatedProductsResultsGCP(restOptions, axiosInstance);
  }
  return getAffinityRelatedProductsResultsACES(restOptions, axiosInstance);
});

export const useAffinityRelatedProductsACES = ({
  enabled = true,
}: { enabled?: boolean; itemsToDisplay?: number } = {}) => {
  const country = useCountry();
  const isBot = useIsBot();
  const locale = useLocale();
  const { acesEnabled, partsProFlag } = useAcesFlags();
  const useGCPRelatedPartsService = useMonetateDecisionFlag('useGCPRelatedPartsService');
  const isElasticEnabled = useFeatureFlag('IS_AFFINITY_ELASTIC_ENABLED') === 'true';
  const { useOMSInventoryQohService } = useOMSFlags();
  const { data: productDetailsData, isSuccess: isProductDetailsSuccess } = useProductDetails({
    enabled: enabled && acesEnabled && !partsProFlag,
  });
  const product = productDetailsData?.product as ProductModelACES | undefined;
  const { itemId, partGroupId = '', vehicleFit, recordType } = product ?? {};
  const { vehicleMap, storeNumber: storeId } = useHeaderData()?.data || {};
  const { year, makeId, modelId, vehicleTypeId } = vehicleMap || {};
  const isVehicleSelected = !!vehicleTypeId;
  const vehicleQuestions = extractVehicleQuestions(vehicleMap?.vehicleQuestionsIds, true) ?? '';
  const { data: partSkuDetailsData } = useProductSkuDetails({
    skuIds: isProductDetailsSuccess && itemId ? [itemId] : null,
  });
  const isPartAvailable =
    partSkuDetailsData?.[0]?.fulfillmentOptions?.some(({ available }) => available) ?? false;
  const isAffinityAllowed = isVehicleSelected
    ? vehicleFit !== 'DOES_NOT_FIT'
    : recordType?.toLowerCase() !== 'app' && vehicleFit !== 'MAY_NOT_FIT';
  const queryEnabled =
    enabled && !isBot && isPartAvailable && isAffinityAllowed && locale !== countryCodes.ptBr;

  const inventorySource = useOMSInventoryQohService ? 'OMS' : 'CIS';

  const optionsACES: AffinityRelatedProductsOptionsACES & { useGCPRelatedPartsService?: boolean } =
    {
      // required
      partGroupId,
      country,
      // optional
      year: year ? String(year) : undefined,
      makeId: makeId ? String(makeId) : undefined,
      modelId: modelId ? String(modelId) : undefined,
      storeId,
      vehicleTypeId: vehicleTypeId ? String(vehicleTypeId) : undefined,
      vehicleQuestions: (vehicleQuestions as string) || undefined,
      ...(useGCPRelatedPartsService && {
        inventorySource,
        useGCPRelatedPartsService,
        useElasticSearch: isElasticEnabled,
      }),
    };

  return useAffinityData({
    ...optionsACES,
    enabled: Boolean(queryEnabled),
  });
};

export const usePrefetchAffinityACES = () => {
  const router = useLegacyRouter();
  const country = useCountry();
  const locale = useLocale();
  const isBot = useIsBot();
  const { acesEnabled } = useAcesFlags();
  const useGCPRelatedPartsService = useMonetateDecisionFlag('useGCPRelatedPartsService');
  const isElasticEnabled = useFeatureFlag('IS_AFFINITY_ELASTIC_ENABLED') === 'true';
  const { useOMSInventoryQohService } = useOMSFlags();
  const { vehicleMap, storeNumber: storeId } = useHeaderData()?.data || {};
  const { year, makeId, modelId, vehicleTypeId } = vehicleMap || {};
  const vehicleQuestions = extractVehicleQuestions(vehicleMap?.vehicleQuestionsIds, true) ?? '';

  const { productPartGroupId } = router.query;
  const partGroupId = Array.isArray(productPartGroupId)
    ? productPartGroupId[0]
    : productPartGroupId;

  const affinityContext = useMemo(() => {
    if (!partGroupId) {
      return null;
    }
    const stored = sessionStorage.getItem('pdpAffinityContext');
    if (!stored) {
      return null;
    }
    try {
      const parsed = JSON.parse(stored) as {
        recordType: string;
        vehicleFitment?: string;
        partGroupId?: string;
      };
      return parsed.partGroupId === partGroupId ? parsed : null;
    } catch {
      return null;
    }
  }, [partGroupId]);

  const normalizedRecordType = affinityContext?.recordType?.toLowerCase();
  const isAffinityAllowed = affinityContext
    ? normalizedRecordType !== 'app' ||
      (affinityContext.vehicleFitment !== 'NEEDS_BASE_VEHICLE' &&
        affinityContext.vehicleFitment !== 'DOES_NOT_FIT')
    : false;

  const inventorySource: 'OMS' | 'CIS' = useOMSInventoryQohService ? 'OMS' : 'CIS';

  return useAffinityData({
    partGroupId: partGroupId ?? '',
    country,
    year: year ? String(year) : undefined,
    makeId: makeId ? String(makeId) : undefined,
    modelId: modelId ? String(modelId) : undefined,
    storeId,
    vehicleTypeId: vehicleTypeId ? String(vehicleTypeId) : undefined,
    vehicleQuestions: (vehicleQuestions as string) || undefined,
    ...(useGCPRelatedPartsService && {
      inventorySource,
      useGCPRelatedPartsService,
      useElasticSearch: isElasticEnabled,
    }),
    enabled: Boolean(
      router.isReady &&
      acesEnabled &&
      !isBot &&
      partGroupId &&
      isAffinityAllowed &&
      locale !== countryCodes.ptBr,
    ),
  } as AffinityRelatedProductsOptionsACES & { useGCPRelatedPartsService?: boolean });
};
